import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSelectedCity } from '../contexts/SelectedCityContext';
import { apiGet, apiPost, apiDelete, fetchAllPaginated } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import SearchableSelect from '../components/SearchableSelect';

/**
 * Calendário Letivo — os dias em que não há aula.
 *
 * Existe para o acompanhamento de diários não acusar feriado como pendência.
 * Um dia marcado aqui deixa de gerar aulas esperadas para as turmas afetadas.
 *
 * Escopo: sem escola escolhida, o dia vale para toda a rede do município;
 * com escola, só para ela (evento local, sala interditada).
 */

const DAY_TYPES = [
  { value: 'feriado', label: 'Feriado', color: 'bg-[#E83A3C]' },
  { value: 'recesso', label: 'Recesso / Férias', color: 'bg-[#0974BA]' },
  { value: 'formacao', label: 'Formação Continuada', color: 'bg-[#7C3AED]' },
  { value: 'suspensao', label: 'Suspensão de Aulas', color: 'bg-[#F9B047]' },
  { value: 'evento', label: 'Evento Escolar', color: 'bg-[#13983D]' },
];

const TYPE_META = Object.fromEntries(DAY_TYPES.map(t => [t.value, t]));

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function CalendarioLetivo() {
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const { selectedCityId } = useSelectedCity();

  const hoje = new Date();
  const [month, setMonth] = useState(hoje.getMonth());
  const [year, setYear] = useState(hoje.getFullYear());

  const [escolas, setEscolas] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [idSchool, setIdSchool] = useState('');
  const [idCity, setIdCity] = useState('');

  const [dias, setDias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Seleção múltipla: marcar uma semana inteira de recesso em um gesto.
  const [selecionados, setSelecionados] = useState([]);
  const [dayType, setDayType] = useState('feriado');
  const [description, setDescription] = useState('');
  const [suspends, setSuspends] = useState(true);

  const canEdit = [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA].includes(user?.role);

  const card = darkMode
    ? 'bg-slate-800/50 border border-slate-700/10'
    : 'bg-white/80 border border-slate-200 shadow-sm';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
  const strong = darkMode ? 'text-slate-100' : 'text-slate-800';
  const inputCls = `px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
    darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
  }`;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [escolasData, cidadesData] = await Promise.all([
          fetchAllPaginated('/schools/').catch(() => []),
          fetchAllPaginated('/cities/').catch(() => []),
        ]);
        if (!alive) return;
        setEscolas(escolasData);
        setCidades(cidadesData);
        // O município já pode ter sido escolhido no menu lateral: partir
        // daquele evita marcar feriado na rede errada por descuido.
        if (user?.city) setIdCity(String(user.city));
        else if (selectedCityId) setIdCity(String(selectedCityId));
        else if (cidadesData.length === 1) setIdCity(String(cidadesData[0].id));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user, selectedCityId]);

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;

  const fetchDias = useCallback(async () => {
    try {
      const params = { month: monthStr };
      if (idSchool) params.id_school = idSchool;
      const resp = await apiGet('/school-calendar/month/', params);
      setDias(resp.results || []);
    } catch (err) {
      console.error('Erro ao carregar o calendário:', err);
      setDias([]);
    }
  }, [monthStr, idSchool]);

  useEffect(() => { fetchDias(); }, [fetchDias]);

  const porData = useMemo(() => {
    const map = {};
    dias.forEach(d => { map[d.date] = d; });
    return map;
  }, [dias]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = new Date(year, month, 1).getDay();
  const dateStrFor = (day) =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const toggleDia = (dateStr) => {
    if (!canEdit) return;
    setSuccess(null);
    setSelecionados(prev => (
      prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]
    ));
  };

  const handleMarcar = async () => {
    if (!selecionados.length) return;
    const cidade = idCity || (user?.city ? String(user.city) : '');
    if (!cidade) {
      setError('Selecione o município.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await apiPost('/school-calendar/bulk-mark/', {
        id_city: parseInt(cidade, 10),
        id_school: idSchool ? parseInt(idSchool, 10) : null,
        dates: selecionados,
        day_type: dayType,
        description: description || null,
        suspends_classes: suspends,
      });
      setSuccess(`${resp.created} dia(s) marcado(s), ${resp.updated} atualizado(s).`);
      setSelecionados([]);
      setDescription('');
      await fetchDias();
    } catch (err) {
      console.error('Erro ao marcar dias:', err);
      setError(err.message || 'Erro ao marcar os dias no calendário.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemover = async (dia) => {
    setSaving(true);
    setError(null);
    try {
      await apiDelete(`/school-calendar/${dia.id}/`);
      await fetchDias();
      setSuccess(`${dia.date.split('-').reverse().join('/')} voltou a ser dia letivo.`);
    } catch (err) {
      console.error('Erro ao remover o dia:', err);
      setError(err.message || 'Erro ao remover o dia do calendário.');
    } finally {
      setSaving(false);
    }
  };

  const shiftMonth = (delta) => {
    setSelecionados([]);
    const d = new Date(year, month + delta, 1);
    setMonth(d.getMonth());
    setYear(d.getFullYear());
  };

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Calendário Letivo..."
        subtitle="Buscando escolas e dias marcados..."
        size="xl"
      />
    );
  }

  return (
    <>
      <header className="mb-6">
        <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
          ? 'bg-gradient-to-r from-gray-50 to-gray-400'
          : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
        }`}>
          Calendário Letivo
        </h1>
        <p className={`text-sm mt-1 ${muted}`}>
          Os dias marcados aqui deixam de cobrar diário no acompanhamento.
        </p>
      </header>

      <div className={`rounded-xl p-4 mb-6 ${darkMode ? 'bg-[#0974BA]/10 border border-[#0974BA]/20' : 'bg-[#0974BA]/5 border border-[#0974BA]/20'}`}>
        <p className={`text-sm ${strong}`}>
          Sem o calendário, cada feriado apareceria como uma enxurrada de aulas pendentes
          no painel da coordenação.
        </p>
      </div>

      <div className={`rounded-xl p-4 mb-6 ${card}`}>
        <div className="flex flex-wrap items-end gap-4">
          {!user?.city && (
            <div className="min-w-[180px]">
              <label className={`block text-xs font-medium mb-1 ${muted}`}>Município</label>
              <SearchableSelect
                value={idCity}
                onChange={(v) => { setIdCity(v); setIdSchool(''); }}
                options={cidades.map(c => ({ value: c.id, label: `${c.city} — ${c.state}` }))}
                emptyOptionLabel="Selecione..."
                searchPlaceholder="Buscar município..."
                darkMode={darkMode}
              />
            </div>
          )}

          <div className="min-w-[240px]">
            <label className={`block text-xs font-medium mb-1 ${muted}`}>Escola</label>
            <SearchableSelect
              value={idSchool}
              onChange={(v) => { setIdSchool(v); setSelecionados([]); }}
              options={escolas
                .filter(e => !idCity || String(e.id_city) === String(idCity))
                .map(e => ({ value: e.id, label: e.school_name || e.school }))}
              emptyOptionLabel="Toda a rede do município"
              searchPlaceholder="Buscar escola..."
              darkMode={darkMode}
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => shiftMonth(-1)}
              className={`px-2.5 py-2 mt-5 rounded-lg ${darkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'}`}
            >
              ‹
            </button>
            <div className={`mt-5 font-semibold text-sm min-w-[140px] text-center ${strong}`}>
              {MONTH_NAMES[month]} {year}
            </div>
            <button
              onClick={() => shiftMonth(1)}
              className={`px-2.5 py-2 mt-5 rounded-lg ${darkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'}`}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className={`p-4 rounded-xl mb-4 ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
          <p className="text-sm text-[#E83A3C]">{error}</p>
        </div>
      )}
      {success && (
        <div className={`p-4 rounded-xl mb-4 ${darkMode ? 'bg-[#13983D]/10 border border-[#13983D]/20' : 'bg-[#13983D]/5 border border-[#13983D]/20'}`}>
          <p className="text-sm text-[#13983D]">{success}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`lg:col-span-2 rounded-xl p-5 ${card}`}>
          <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${muted}`}>
            {canEdit ? 'Clique nos dias para selecionar' : 'Dias sem aula'}
          </h3>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEK_DAYS.map(d => (
              <div key={d} className={`text-center text-xs font-semibold py-2 ${muted}`}>{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstWeekday }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square" />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = dateStrFor(day);
              const marcado = porData[dateStr];
              const selecionado = selecionados.includes(dateStr);
              const meta = marcado ? TYPE_META[marcado.day_type] : null;

              return (
                <button
                  key={day}
                  onClick={() => toggleDia(dateStr)}
                  disabled={!canEdit}
                  title={marcado
                    ? `${marcado.day_type_display}${marcado.description ? ` — ${marcado.description}` : ''} (${marcado.scope})`
                    : canEdit ? 'Clique para selecionar' : ''}
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm font-medium transition-all ${
                    canEdit ? 'hover:scale-105' : 'cursor-default'
                  } ${
                    selecionado
                      ? 'ring-2 ring-[#0974BA] bg-[#0974BA]/20 text-[#0974BA]'
                      : meta
                      ? `${meta.color} text-white`
                      : darkMode
                      ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {day}
                  {marcado && !marcado.suspends_classes && (
                    <span className="text-[9px] leading-none opacity-90">c/ aula</span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-slate-600/30">
            {DAY_TYPES.map(t => (
              <div key={t.value} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded ${t.color}`} />
                <span className={`text-xs ${muted}`}>{t.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-6">
          {canEdit && (
            <div className={`rounded-xl p-5 ${card}`}>
              <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${muted}`}>
                Marcar {selecionados.length || 'os'} dia(s) selecionado(s)
              </h3>

              <label className={`block text-xs font-medium mb-1 ${muted}`}>Tipo</label>
              <SearchableSelect
                value={dayType}
                onChange={setDayType}
                options={DAY_TYPES.map(t => ({ value: t.value, label: t.label }))}
                required
                darkMode={darkMode}
                className="mb-3"
              />

              <label className={`block text-xs font-medium mb-1 ${muted}`}>Descrição</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex.: Padroeira do município"
                className={`${inputCls} w-full mb-3`}
              />

              <label className={`flex items-start gap-2 text-xs mb-4 ${muted}`}>
                <input
                  type="checkbox"
                  checked={suspends}
                  onChange={(e) => setSuspends(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Suspende as aulas.
                  <span className="block opacity-80">
                    Desmarque para um evento que ocupa só parte do dia — a cobrança
                    do diário continua valendo.
                  </span>
                </span>
              </label>

              <button
                onClick={handleMarcar}
                disabled={saving || !selecionados.length}
                className={`w-full px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${
                  saving || !selecionados.length
                    ? 'bg-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25'
                }`}
              >
                {saving ? 'Salvando...' : `Marcar ${selecionados.length} dia(s)`}
              </button>
            </div>
          )}

          <div className={`rounded-xl p-5 ${card}`}>
            <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${muted}`}>
              {MONTH_NAMES[month]} — {dias.length} dia(s)
            </h3>
            {dias.length === 0 ? (
              <p className={`text-sm ${muted}`}>Nenhum dia marcado neste mês.</p>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {dias.map(d => {
                  const meta = TYPE_META[d.day_type];
                  return (
                    <div
                      key={d.id}
                      className={`flex items-start gap-3 p-3 rounded-lg ${darkMode ? 'bg-slate-700/40' : 'bg-slate-50'}`}
                    >
                      <span className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${meta?.color || 'bg-slate-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${strong}`}>
                          {d.date.split('-').reverse().join('/')}
                        </p>
                        <p className={`text-xs ${muted}`}>
                          {d.day_type_display}
                          {d.description ? ` — ${d.description}` : ''}
                        </p>
                        <p className={`text-[11px] ${muted}`}>
                          {d.scope === 'rede' ? 'Toda a rede' : d.school_name}
                          {!d.suspends_classes && ' • não suspende aulas'}
                        </p>
                      </div>
                      {canEdit && (
                        <button
                          onClick={() => handleRemover(d)}
                          disabled={saving}
                          title="Voltar a ser dia letivo"
                          className="px-2 py-1 rounded text-xs font-medium bg-[#E83A3C]/15 text-[#E83A3C] hover:bg-[#E83A3C]/25 flex-shrink-0"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default CalendarioLetivo;
